import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAgentStore } from '../store/useAgentStore'
import { apiClient } from '../api/client'

interface TokenResponse {
  access_token: string
  refresh_token: string
  user: { id: number; email: string; full_name: string; role: string; language: string }
}

export default function Login() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const setUser = useAgentStore((s) => s.setUser)
  const setToken = useAgentStore((s) => s.setToken)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !password) return
    setBusy(true)
    setError(null)
    try {
      // OAuth2 password flow — body كوسيط ثانٍ صراحةً (config.data لا يعمل بمتصفح axios)
      const res = await apiClient.post<TokenResponse>(
        '/auth/login',
        new URLSearchParams({ username: email.trim(), password }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
      )
      const { access_token, refresh_token, user } = res.data
      setToken(access_token)
      setUser(user)
      localStorage.setItem('refresh_token', refresh_token)
      localStorage.setItem('access_token', access_token)
      navigate('/dashboard', { replace: true })
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } }
      setError(e.response?.data?.detail || t('auth.login_failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <form onSubmit={handleSubmit} className="card w-full max-w-md space-y-5">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-primary-700 dark:text-primary-400">
            {t('app.name')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2">{t('auth.login')}</p>
        </div>

        <div>
          <label htmlFor="login-email" className="label">{t('auth.email')}</label>
          <input
            id="login-email"
            type="email"
            className="input-field"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </div>

        <div>
          <label htmlFor="login-password" className="label">{t('auth.password')}</label>
          <input
            id="login-password"
            type="password"
            className="input-field"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        {error && (
          <p role="alert" className="text-danger-600 font-bold">{error}</p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {busy ? t('common.loading') : t('auth.login')}
        </button>
      </form>
    </div>
  )
}
